// Het filtermenu. Drie vormen, één component:
//
//  · smal      — de dummy-dropdown (V1 r. 762), goed voor drie tot vijf opties
//  · breed     — een groot paneel in kolommen, voor een as met 25 waarden
//  · gegroepeerd — één breed paneel met MEERDERE assen naast elkaar, elk met
//    een eigen kopje. Nodig op de Bronnen-tab: W8 §4 keurde vijftien filters
//    goed en vijftien knoppen naast elkaar is dezelfde sliert waar de owner bij
//    de categorieën over viel.
//
// Owner-feedback (opname 2026-08-24, 0:28-0:57): "het is een hele lange lijst,
// dat filtert niet lekker — maak er een breed menu van, zo groot als de
// filterbalk". Een lijst van 25 categorieën in een kolom van 210px is geen
// menu maar een sliert.
//
// OWNER-REVIEW RONDE 4 (Bronnen #2 "andere filters zijn lastig filterbaar" en
// #3 "zet de omschrijving in een i'tje"). De gemeten oorzaak van #2: de as
// UITGEVER heeft 146 waarden en stond als één scrollkolom naast een as met één
// waarde. Vier ingrepen, alle vier op dezelfde klacht:
//
//   1. een zoekregel binnen een as zodra hij meer dan TOON_DIRECT waarden heeft
//   2. gekozen waarden bovenaan — anders raak je kwijt wat je hebt aangevinkt
//      zodra je scrolt of zoekt
//   3. lange assen tonen de eerste vijftien met "toon alle 146" eronder; de
//      telling staat er altijd bij, dus je weet wat je niet ziet
//   4. de kolomindeling volgt het aantal assen in plaats van altijd drie
//
// En #3: de uitleg stond als grijze zin in de menukop en per as helemaal
// nergens. Nu hangt hij aan een i'tje naast de naam — bij de groep én bij elke
// as afzonderlijk.

import { useEffect, useMemo, useRef, useState } from "react";

import { InfoIcon } from "./ui";
import { color, radius, size, weight } from "../tokens";

/** Vanaf hoeveel waarden een as een eigen zoekregel krijgt. */
const ZOEK_VANAF = 12;
/** Hoeveel waarden een lange as direct toont voordat je "toon alle" klikt. */
const TOON_DIRECT = 15;

/** Eén aankruisbare waarde. Draagt zijn aantal; niet-kiesbaar staat grijs mét
 *  reden op de tooltip. */
function Optie({
  opt,
  sel,
  off,
  n,
  wide,
  onToggle,
}: {
  opt: string;
  sel: boolean;
  off?: string;
  n?: number;
  wide: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      title={off}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: "100%",
        padding: wide ? "9px 10px" : "11px 15px",
        background: sel ? color.surfaceQuote : "transparent",
        border: "none",
        borderRadius: wide ? radius.quote : 0,
        borderBottom: wide ? "none" : `1px solid ${color.surfaceQuote}`,
        color: off ? color.disabled : sel ? color.ink : color.body,
        fontSize: wide ? size.meta : size.body,
        fontFamily: "inherit",
        cursor: off ? "not-allowed" : "pointer",
        textAlign: "left",
        transition: "background 0.12s",
      }}
      onMouseEnter={(e) => {
        // Hover moet zichtbaar zijn — de owner miste hem (opname 0:28).
        if (!off) e.currentTarget.style.background = color.lineFaint;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = sel ? color.surfaceQuote : "transparent";
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          border: `1.5px solid ${sel ? color.ink : off ? "#ededed" : color.disabled}`,
          background: sel ? color.ink : color.surface,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {sel && (
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
            <path
              d="M1.5 4.5l2.5 2.5 4-4"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {opt}
      </span>
      {typeof n === "number" && (
        <span style={{ fontSize: 12, color: n ? color.faint : color.disabled, flexShrink: 0 }}>
          {n}
        </span>
      )}
    </button>
  );
}

export type FilterSection = {
  /** Sleutel van de as; alleen voor React. */
  key: string;
  /** Kopje boven de kolom. */
  label: string;
  /** Wat deze as meet — komt in het i'tje naast het kopje. */
  uitleg?: string;
  options: readonly string[];
  values: readonly string[];
  counts?: Readonly<Record<string, number>>;
  disabled?: Readonly<Record<string, string>>;
  onChange: (next: string[]) => void;
  /** Eigen inhoud in plaats van een aankruislijst (de datumkiezer). */
  render?: () => React.ReactNode;
  /** true = het kopje wordt een knop die de hele as aan/uit zet. Nodig voor de
   *  onderwerp-as: de owner vroeg "Regelgeving in één klik" én NIS2 apart. */
  allesTegelijk?: boolean;
};

/**
 * De lijstlogica van één as: zoeken binnen de as, gekozen waarden bovenaan,
 * lange lijsten ingekort.
 *
 * Zit hier als hook en niet in één van de twee menuvormen, want de klacht geldt
 * voor allebei: gemeten heeft Bronnen·Uitgever 146 waarden (gegroepeerd menu),
 * maar Trends·Categorie er 36 en Skills·Categorie 29 in het BREDE menu. Bij de
 * eerste poging landde de verbetering alleen op de gegroepeerde vorm — dan is
 * dezelfde klacht op twee schermen nog steeds waar.
 */
function useOptieLijst(options: readonly string[], values: readonly string[]) {
  const [zoek, setZoek] = useState("");
  const [alles, setAlles] = useState(false);

  const gesorteerd = useMemo(() => {
    // Gekozen waarden bovenaan: die wil je nooit kwijtraken bij het scrollen.
    const gekozen = options.filter((o) => values.includes(o));
    const rest = options.filter((o) => !values.includes(o));
    return [...gekozen, ...rest];
  }, [options, values]);

  const gefilterd = zoek
    ? gesorteerd.filter((o) => o.toLowerCase().includes(zoek.toLowerCase()))
    : gesorteerd;
  const kort = alles || zoek ? gefilterd : gefilterd.slice(0, TOON_DIRECT);
  return {
    zoek,
    setZoek,
    zichtbaar: kort,
    verborgen: gefilterd.length - kort.length,
    gevonden: gefilterd.length,
    alles,
    setAlles,
    zoekbaar: options.length > ZOEK_VANAF,
  };
}

/** De zoekregel binnen één as. */
function AsZoek({
  aantal,
  waarde,
  onChange,
}: {
  aantal: number;
  waarde: string;
  onChange: (v: string) => void;
}) {
  return (
    <input
      value={waarde}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`filter deze ${aantal}…`}
      style={{
        width: "100%",
        marginBottom: 6,
        padding: "6px 9px",
        border: `1px solid ${color.border}`,
        borderRadius: radius.quote,
        fontSize: 12,
        fontFamily: "inherit",
        color: color.body,
        outline: "none",
      }}
    />
  );
}

/** "toon alle N" / "inklappen" onder een ingekorte as. */
function MeerMinder({
  verborgen,
  gevonden,
  alles,
  zoek,
  setAlles,
}: {
  verborgen: number;
  gevonden: number;
  alles: boolean;
  zoek: string;
  setAlles: (v: boolean) => void;
}) {
  const knop = (tekst: string, kleur: string, naar: boolean) => (
    <button
      onClick={() => setAlles(naar)}
      style={{
        background: "none",
        border: "none",
        color: kleur,
        fontSize: 12,
        fontFamily: "inherit",
        cursor: "pointer",
        padding: "6px 10px",
        textAlign: "left",
      }}
    >
      {tekst}
    </button>
  );
  if (verborgen > 0) return knop(`toon alle ${gevonden}`, color.accentBlue, true);
  if (alles && !zoek && gevonden > TOON_DIRECT) return knop("inklappen", color.faint, false);
  return null;
}

/** Eén as binnen een gegroepeerd menu: kopje, zoekregel, gekozen bovenaan, inkorting. */
function Sectie({ sec }: { sec: FilterSection }) {
  const lijst = useOptieLijst(sec.options, sec.values);
  const { zoek, setZoek, zichtbaar: kort, verborgen, alles, setAlles } = lijst;
  const gefilterd = { length: lijst.gevonden };

  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          marginBottom: 6,
          paddingBottom: 4,
          borderBottom: `1px solid ${color.lineFaint}`,
        }}
      >
        {sec.allesTegelijk ? (
          <button
            onClick={() =>
              sec.onChange(
                sec.values.length === sec.options.length ? [] : [...sec.options],
              )
            }
            title={
              sec.values.length === sec.options.length
                ? `Alle ${sec.options.length} weer uitzetten`
                : `Alle ${sec.options.length} in één klik aanzetten`
            }
            style={{
              background: "none",
              border: "none",
              padding: 0,
              fontSize: 11,
              fontWeight: weight.medium,
              color: sec.values.length ? color.ink : color.soft,
              fontFamily: "inherit",
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
              textDecorationColor: color.disabled,
            }}
          >
            {sec.label}
          </button>
        ) : (
          <span style={{ fontSize: 11, fontWeight: weight.medium, color: color.soft }}>
            {sec.label}
          </span>
        )}
        {sec.uitleg && <InfoIcon tooltip={sec.uitleg} />}
        <span style={{ fontSize: 11, color: color.disabled, marginLeft: "auto" }}>
          {sec.options.length}
        </span>
      </div>

      {sec.render ? (
        sec.render()
      ) : sec.options.length === 0 ? (
        <span style={{ fontSize: 12, color: color.disabled }}>geen waarden</span>
      ) : (
        <>
          {lijst.zoekbaar && (
            <AsZoek aantal={sec.options.length} waarde={zoek} onChange={setZoek} />
          )}
          {kort.map((opt) => (
            <Optie
              key={opt}
              opt={opt}
              sel={sec.values.includes(opt)}
              off={sec.disabled?.[opt]}
              n={sec.counts?.[opt]}
              wide
              onToggle={() => {
                if (sec.disabled?.[opt]) return;
                sec.onChange(
                  sec.values.includes(opt)
                    ? sec.values.filter((v) => v !== opt)
                    : [...sec.values, opt],
                );
              }}
            />
          ))}
          {zoek && gefilterd.length === 0 && (
            <span style={{ fontSize: 12, color: color.disabled, padding: "6px 10px" }}>
              geen waarde met “{zoek}”
            </span>
          )}
          <MeerMinder
            verborgen={verborgen}
            gevonden={lijst.gevonden}
            alles={alles}
            zoek={zoek}
            setAlles={setAlles}
          />
        </>
      )}
    </div>
  );
}

export function FilterMenu({
  label,
  options = [],
  values = [],
  onChange,
  counts,
  disabled,
  note,
  sections,
}: {
  label: string;
  options?: readonly string[];
  values?: readonly string[];
  onChange?: (next: string[]) => void;
  counts?: Readonly<Record<string, number>>;
  disabled?: Readonly<Record<string, string>>;
  note?: string;
  /** Genegeerd sinds ronde 6: élk menu is breed en opent op dezelfde plek. De
   *  prop blijft geaccepteerd omdat een parallelle sessie hem nog meegeeft
   *  (ArbeidsmarktPage) — hem nu schrappen zou hún scherm breken, en dat is
   *  niet aan deze sessie. Kan weg zodra die sessie klaar is. */
  wide?: boolean;
  /** Meerdere assen in één paneel; overstemt options/values. */
  sections?: readonly FilterSection[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Altijd aanroepen, ook als dit menu `sections` gebruikt: een hook mag niet
  // achter een voorwaarde staan. Bij een gegroepeerd menu blijft hij ongebruikt.
  const lijst = useOptieLijst(options, values);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, []);

  const gekozen = sections
    ? sections.reduce((n, sec) => n + sec.values.length, 0)
    : values.length;
  const active = gekozen > 0;
  // OWNER-REVIEW RONDE 6: "de filter dropdown altijd op dezelfde plek (zoals
  // uitgever)". Tot nu bepaalde de AS waar het paneel opende — een lange as
  // hing aan de filterkaart en liep van rand tot rand, een korte opende onder
  // zijn eigen knop. Daardoor sprong het menu per filter. Nu opent élk menu op
  // dezelfde plek en met dezelfde breedte; een korte as krijgt dan een paneel
  // met witruimte, en dat is de prijs van voorspelbaarheid.
  const breed = true;
  const toggle = (opt: string) => {
    if (disabled?.[opt]) return;
    onChange?.(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt]);
  };
  const wisAlles = () => {
    if (sections) sections.forEach((sec) => sec.onChange([]));
    else onChange?.([]);
  };

  return (
    // Een breed menu hangt aan de FILTERKAART (die is position: relative), niet
    // aan zijn eigen knop — zo loopt het van rand tot rand en passen ook de
    // lange labels erin. Owner 2026-08-24: "ik wil een menu zo breed als die
    // balk, want nu zie ik het aantal bij DevOps & Systems Administration niet."
    // `position: static` zodat het paneel zich meet aan de FILTERKAART (die is
    // relative) en niet aan deze knop — dat is wat "altijd dezelfde plek" doet.
    <div ref={ref} style={{ position: "static", flexShrink: 0 }}>
      <button
        onClick={() => setOpen(!open)}
        className="filter-btn"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: active ? color.ink : color.surface,
          border: `1.5px solid ${active ? color.ink : color.border}`,
          color: active ? color.surface : color.body,
          padding: "9px 15px",
          borderRadius: radius.input,
          fontSize: size.lead,
          fontWeight: weight.regular,
          fontFamily: "inherit",
          cursor: "pointer",
          whiteSpace: "nowrap",
          transition: "all 0.15s",
          minWidth: 110,
          justifyContent: "space-between",
        }}
      >
        <span>{active ? `${label} (${gekozen})` : label}</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "none",
            flexShrink: 0,
          }}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            background: color.surface,
            border: `1px solid ${color.border}`,
            borderRadius: breed ? radius.card : radius.block,
            boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
            zIndex: 200,
            minWidth: undefined,
            overflow: "hidden",
          }}
        >
          {(note || breed) && (
            <div
              style={{
                padding: breed ? "12px 18px 10px" : "9px 15px",
                borderBottom: `1px solid ${color.lineFaint}`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: size.label,
                  fontWeight: weight.medium,
                  color: color.faint,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {label}
              </span>
              {/* De uitleg zat als grijze zin in deze kop en drukte de kolommen
                  omlaag; nu hangt hij aan het i'tje (owner-review, Bronnen #3). */}
              {note && <InfoIcon tooltip={note} maxWidth={280} />}
              {active && (
                <button
                  onClick={wisAlles}
                  style={{
                    background: "none",
                    border: "none",
                    color: color.faint,
                    fontSize: size.meta,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    flexShrink: 0,
                    marginLeft: "auto",
                  }}
                >
                  wissen
                </button>
              )}
            </div>
          )}

          {sections ? (
            // Gegroepeerd: één kolom per as, elk met zijn eigen kopje. De
            // kolombreedte volgt het AANTAL assen — met altijd drie kolommen
            // stond een as met één waarde naast een as met 146.
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(4, sections.length)}, minmax(0, 1fr))`,
                gap: 16,
                padding: 14,
                maxHeight: "min(68vh, 520px)",
                overflowY: "auto",
                alignItems: "start",
              }}
            >
              {sections.map((sec) => (
                <Sectie key={sec.key} sec={sec} />
              ))}
            </div>
          ) : (
            <div style={{ padding: 10 }}>
              {/* Dezelfde behandeling als in het gegroepeerde menu: zoeken
                  binnen de as, gekozen bovenaan, lange lijsten ingekort.
                  Gemeten heeft Trends·Categorie 36 waarden en Skills·Categorie
                  29 — allebei in dit brede menu, allebei scrollend zolang deze
                  regels er alleen in de gegroepeerde vorm stonden. */}
              {lijst.zoekbaar && (
                <AsZoek aantal={options.length} waarde={lijst.zoek} onChange={lijst.setZoek} />
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
                  gap: 2,
                  maxHeight: "min(60vh, 460px)",
                  overflowY: "auto",
                }}
              >
                {lijst.zichtbaar.map((opt) => (
                  <Optie
                    key={opt}
                    opt={opt}
                    sel={values.includes(opt)}
                    off={disabled?.[opt]}
                    n={counts?.[opt]}
                    wide
                    onToggle={() => toggle(opt)}
                  />
                ))}
              </div>
              {lijst.zoek && lijst.gevonden === 0 && (
                <span style={{ fontSize: 12, color: color.disabled, padding: "6px 10px" }}>
                  geen waarde met “{lijst.zoek}”
                </span>
              )}
              {(
                <MeerMinder
                  verborgen={lijst.verborgen}
                  gevonden={lijst.gevonden}
                  alles={lijst.alles}
                  zoek={lijst.zoek}
                  setAlles={lijst.setAlles}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
